import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";
import { useRealtime } from "../lib/useRealtime";

type AgentRun = {
  run_id: string;
  tenant_id: string;
  agent: string;
  status: "queued" | "running" | "succeeded" | "failed" | string;
  started_at: string;
  ended_at?: string | null;
  tools_used: string[];
  llm_calls: { model?: string; tokens_in?: number; tokens_out?: number; cost_eur?: number; evidence_id?: string }[];
  trace_url?: string;
};

type AgentMeta = {
  id: string;
  emoji: string;
  display: string;
  goal: string;
  schedule: string;
  owner: "codex" | "cowork" | "platform";
  category: string;
};

const AGENT_REGISTRY: Record<string, AgentMeta> = {
  "roi-monte-carlo": {
    id: "roi-monte-carlo", emoji: "🎲",
    display: "ROI Monte-Carlo",
    goal: "Realistische ROI-Bandbreiten (P10/P50/P90) für jede Investition mit deterministischem Seed berechnen.",
    schedule: "on-demand · per ROI-Page-Trigger",
    owner: "codex", category: "Finance",
  },
  "briefing-generator": {
    id: "briefing-generator", emoji: "📝",
    display: "Briefing-Generator",
    goal: "Wöchentliche CFO/CEO/COO-Briefings mit KPI-Snapshot, Timeline und Empfehlungen verfassen.",
    schedule: "weekly · trigger-on-demand",
    owner: "codex", category: "Communication",
  },
  "export-generator": {
    id: "export-generator", emoji: "📤",
    display: "Export-Generator",
    goal: "Audit-konforme CSV/JSON/PDF-Exports mit Evidence-Manifest produzieren.",
    schedule: "on-demand",
    owner: "codex", category: "Compliance",
  },
  "compliance-fria": {
    id: "compliance-fria", emoji: "🛡️",
    display: "Compliance FRIA",
    goal: "EU-AI-Act Art. 27 Fundamental Rights Impact Assessment Dossiers automatisch generieren.",
    schedule: "monthly · trigger-on-demand",
    owner: "codex", category: "Compliance",
  },
  "audit-reseal": {
    id: "audit-reseal", emoji: "🔐",
    display: "Audit-Reseal",
    goal: "ClickHouse Audit-Chain auf Integritätsverletzungen prüfen und bei Bedarf reseal.",
    schedule: "daily · auto-trigger",
    owner: "codex", category: "Compliance",
  },
  "ki-advisor-v1": {
    id: "ki-advisor-v1", emoji: "💬",
    display: "KI-Berater V1",
    goal: "Persona+Modus-spezifische Antworten via LiteLLM/gemini-2.5-flash mit Audit-Trail.",
    schedule: "user-driven · Chat/Drawer/Cmd+/",
    owner: "platform", category: "Advisory",
  },
  "ki-advisor-v2": {
    id: "ki-advisor-v2", emoji: "💬",
    display: "KI-Berater V2 (Real-LLM)",
    goal: "Echte LLM-Antworten via LiteLLM mit 4 Modi × 12 Personas.",
    schedule: "user-driven",
    owner: "platform", category: "Advisory",
  },
  "kpi-discovery": {
    id: "kpi-discovery", emoji: "🔎",
    display: "KPI Discovery",
    goal: "Aus Datenquellen automatisch KPI-Kandidaten erkennen, mit Datenqualität-Score validiert.",
    schedule: "on-source-sync",
    owner: "codex", category: "Data",
  },
  "data-quality-v1": {
    id: "data-quality-v1", emoji: "✅",
    display: "Daten-Qualität",
    goal: "5-Dimensionen-Quality-Score: Vollständigkeit, Genauigkeit, Konsistenz, Aktualität, Validität.",
    schedule: "on-source-upload",
    owner: "platform", category: "Data",
  },
  "onboarding-research-v1": {
    id: "onboarding-research-v1", emoji: "🔬",
    display: "Onboarding-Research",
    goal: "Crawl4AI + Wikidata + Branche-Benchmark + LLM-Synthese während Onboarding.",
    schedule: "per /onboard/start",
    owner: "platform", category: "Onboarding",
  },
  "onboarding-orchestrator": {
    id: "onboarding-orchestrator", emoji: "🚀",
    display: "Onboarding-Orchestrator",
    goal: "Multi-Step-Conversation steuern und 5 maßgeschneiderte KPIs vorschlagen.",
    schedule: "per /onboard/start",
    owner: "platform", category: "Onboarding",
  },
};

function genericMeta(id: string): AgentMeta {
  return {
    id, emoji: "🤖", display: id,
    goal: "Autonom — Codex-managed.",
    schedule: "managed",
    owner: "codex", category: "Misc",
  };
}

function fmtDuration(start: string, end?: string | null): string {
  if (!end) return "läuft";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}min`;
}

function fmtTimeAgo(iso: string): string {
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return `vor ${sec}s`;
  if (sec < 3600) return `vor ${Math.floor(sec / 60)}min`;
  if (sec < 86400) return `vor ${Math.floor(sec / 3600)}h`;
  return `vor ${Math.floor(sec / 86400)}T`;
}

export default function AgentsLivePage() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const { connected, events } = useRealtime();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<string>("all");

  const runs = useQuery({
    queryKey: ["agent-runs-live", effectiveTenantId],
    queryFn: () => api.get<AgentRun[]>("/agent-runs"),
    refetchInterval: 12_000,
  });

  // Invalidate on realtime events
  useEffect(() => {
    if (events.length > 0 && events[0].type === "agent_run") {
      qc.invalidateQueries({ queryKey: ["agent-runs-live"] });
    }
  }, [events.length, qc]);

  const grouped = useMemo(() => {
    const map = new Map<string, AgentRun[]>();
    for (const r of (runs.data ?? [])) {
      const arr = map.get(r.agent) || [];
      arr.push(r);
      map.set(r.agent, arr);
    }
    return Array.from(map.entries())
      .map(([agent, list]) => ({ agent, list: list.sort((a, b) => b.started_at.localeCompare(a.started_at)) }))
      .sort((a, b) => b.list[0].started_at.localeCompare(a.list[0].started_at));
  }, [runs.data]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const { agent } of grouped) {
      const meta = AGENT_REGISTRY[agent] || genericMeta(agent);
      set.add(meta.category);
    }
    return ["all", ...Array.from(set).sort()];
  }, [grouped]);

  const filtered = filter === "all" ? grouped : grouped.filter(({ agent }) => (AGENT_REGISTRY[agent] || genericMeta(agent)).category === filter);

  const totalRuns = (runs.data ?? []).length;
  const succeeded = (runs.data ?? []).filter((r) => r.status === "succeeded").length;
  const totalLlmCost = (runs.data ?? []).reduce((sum, r) => sum + (r.llm_calls || []).reduce((s, c) => s + (c.cost_eur || 0), 0), 0);

  return (
    <section className="agents-live-page">
      <header className="cmd-hero">
        <div>
          <p className="hero-eyebrow">Agents Live · Autonomes Squadron</p>
          <h1>Was die Agenten gerade tun</h1>
          <p className="hero-sub muted">
            {connected ? <span className="dot-live">● live</span> : <span className="dot-off">○ offline</span>} ·
            {" "}{grouped.length} Agenten aktiv · {totalRuns} Runs · {succeeded} erfolgreich · {totalLlmCost.toFixed(3)} € LLM-Kosten
          </p>
        </div>
        <div className="hero-stats">
          {categories.map((c) => (
            <button key={c} className={`stat-pill ${filter === c ? "cmdk-hint" : ""}`} onClick={() => setFilter(c)}>
              {c === "all" ? "Alle" : c}
            </button>
          ))}
        </div>
      </header>

      {runs.isLoading && <p className="muted" style={{ padding: 24 }}>Lade Agent-Runs …</p>}
      {runs.error && <p className="error" style={{ padding: 24 }}>Fehler beim Laden.</p>}

      <div className="agents-grid">
        {filtered.map(({ agent, list }) => {
          const meta = AGENT_REGISTRY[agent] || genericMeta(agent);
          const latest = list[0];
          const succ = list.filter((r) => r.status === "succeeded").length;
          const succRate = list.length > 0 ? Math.round((succ / list.length) * 100) : 0;
          const isRecent = (Date.now() - new Date(latest.started_at).getTime()) < 600_000;
          return (
            <article key={agent} className={`agent-card status-${latest.status}`}>
              <header className="agent-card-head">
                <div className="agent-id">
                  <span className="agent-emoji">{meta.emoji}</span>
                  <div>
                    <strong>{meta.display}</strong>
                    <span className={`muted small owner owner-${meta.owner}`}>{meta.owner} · {meta.category}</span>
                  </div>
                </div>
                <span className={`status-badge sev-${latest.status === "succeeded" ? "info" : latest.status === "failed" ? "critical" : "warning"}`}>
                  {isRecent && <span className="act-dot" style={{ marginRight: 6 }} />}
                  {latest.status}
                </span>
              </header>

              <p className="agent-goal">{meta.goal}</p>

              <div className="agent-stats">
                <div>
                  <span className="muted small">Runs</span>
                  <strong>{list.length}</strong>
                </div>
                <div>
                  <span className="muted small">Erfolgsrate</span>
                  <strong>{succRate}%</strong>
                </div>
                <div>
                  <span className="muted small">Zuletzt</span>
                  <strong>{fmtTimeAgo(latest.started_at)}</strong>
                </div>
                <div>
                  <span className="muted small">Dauer</span>
                  <strong>{fmtDuration(latest.started_at, latest.ended_at)}</strong>
                </div>
              </div>

              <details className="agent-detail">
                <summary>Letzte {Math.min(5, list.length)} Runs</summary>
                <ul className="agent-runs-list">
                  {list.slice(0, 5).map((r) => (
                    <li key={r.run_id}>
                      <span className={`status-dot sev-${r.status === "succeeded" ? "info" : r.status === "failed" ? "critical" : "warning"}`}></span>
                      <code className="small muted">{r.run_id.slice(0, 16)}</code>
                      <span className="muted small">{fmtTimeAgo(r.started_at)}</span>
                      {r.tools_used.length > 0 && <span className="muted small">tools: {r.tools_used.slice(0, 3).join(", ")}</span>}
                    </li>
                  ))}
                </ul>
              </details>

              <footer className="agent-card-foot">
                <span className="muted small">{meta.schedule}</span>
                <Link to="/app/agents" className="btn ghost small">Run-Log →</Link>
              </footer>
            </article>
          );
        })}
      </div>

      {grouped.length === 0 && !runs.isLoading && (
        <div style={{ padding: 24 }} className="muted">
          Noch keine Agent-Runs in diesem Tenant. Sobald Codex einen Slice ausführt oder
          jemand den KI-Berater fragt, erscheinen hier Live-Karten.
        </div>
      )}
    </section>
  );
}
